// The identity facts of one client application (SEP-263, slice 7).
//
// Three readers need the SAME few facts out of the answer map: the Credas
// entity that the identity check is run against (SEP-263.45), the prospect
// `Client` the submission creates, and the Stage 1 enquiry it raises
// (SEP-263.23). One derivation serves all three, so the name on the Credas
// entity and the name on the case can never differ.
//
// EVERY FIELD COMES FROM A FROZEN QUESTION ID. The ids below are members of
// `FROZEN_CLIENT_APPLICATION_QUESTION_IDS`, so a rename breaks the build
// before it breaks a submission.
//
// This module holds no vendor shape. It gives plain facts, and the adapter maps
// them onto whatever the vendor asks for.

import type { ClientApplicationAnswers } from './client-application-validation';

/** The address of the applicant, as the form captures it. */
export interface ClientApplicationAddress {
    readonly houseNoName: string;
    readonly street: string;
    readonly townCity: string;
    readonly county: string;
    readonly postcode: string;
    /** ISO 3166-1 alpha-2, from the country dropdown. */
    readonly countryCode: string;
}

/** The identity facts of one applicant. */
export interface ClientApplicationIdentity {
    readonly title: string;
    readonly forenames: string;
    readonly surname: string;
    /** "Forenames Surname", trimmed. Empty when the client answered neither. */
    readonly fullName: string;
    /** An ISO calendar day, or an empty string when the client gave none. */
    readonly dateOfBirth: string;
    readonly email: string;
    /** The mobile number first, then the home number, then the business one. */
    readonly phone: string;
    readonly address: ClientApplicationAddress;
}

/** The question id of each identity fact. A rename breaks the frozen list. */
export const CLIENT_APPLICATION_IDENTITY_QUESTION_IDS = {
    title: 'title',
    forenames: 'forenames',
    surname: 'surname',
    dateOfBirth: 'date_of_birth',
    email: 'email_address',
    mobileNumber: 'mobile_number',
    homeNumber: 'home_number',
    businessNumber: 'business_number',
    houseNoName: 'current_address_house_no_name',
    street: 'current_address_street',
    townCity: 'current_address_town_city',
    county: 'current_address_county',
    postcode: 'current_address_postcode',
    country: 'current_address_country',
} as const;

/** One answer as trimmed text. A non-string answer reads as an empty string. */
function text(answers: ClientApplicationAnswers, questionId: string): string {
    const value = answers[questionId];
    return typeof value === 'string' ? value.trim() : '';
}

/**
 * The identity facts of one application.
 *
 * It never invents a value. A fact the client did not give reads as an empty
 * string, and the caller decides whether that is enough to go on.
 */
export function clientApplicationIdentity(
    answers: ClientApplicationAnswers,
): ClientApplicationIdentity {
    const ids = CLIENT_APPLICATION_IDENTITY_QUESTION_IDS;
    return identityFrom(answers, {
        title: ids.title,
        forenames: ids.forenames,
        surname: ids.surname,
        dateOfBirth: ids.dateOfBirth,
        email: ids.email,
        phones: [ids.mobileNumber, ids.homeNumber, ids.businessNumber],
        houseNoName: ids.houseNoName,
        street: ids.street,
        townCity: ids.townCity,
        county: ids.county,
        postcode: ids.postcode,
        country: ids.country,
    });
}

/** The question id of each identity fact of one person. */
interface IdentityIds {
    readonly title: string;
    readonly forenames: string;
    readonly surname: string;
    readonly dateOfBirth: string;
    readonly email: string;
    /** The phone numbers, in the order that a reader takes the first one. */
    readonly phones: readonly string[];
    readonly houseNoName: string;
    readonly street: string;
    readonly townCity: string;
    readonly county: string;
    readonly postcode: string;
    readonly country: string;
}

function identityFrom(
    answers: ClientApplicationAnswers,
    ids: IdentityIds,
): ClientApplicationIdentity {
    const forenames = text(answers, ids.forenames);
    const surname = text(answers, ids.surname);
    const phone = ids.phones.map((id) => text(answers, id)).find((value) => value !== '') ?? '';
    return {
        title: text(answers, ids.title),
        forenames,
        surname,
        fullName: [forenames, surname].filter(Boolean).join(' '),
        dateOfBirth: text(answers, ids.dateOfBirth),
        email: text(answers, ids.email),
        phone,
        address: {
            houseNoName: text(answers, ids.houseNoName),
            street: text(answers, ids.street),
            townCity: text(answers, ids.townCity),
            county: text(answers, ids.county),
            postcode: text(answers, ids.postcode),
            countryCode: text(answers, ids.country),
        },
    };
}

/**
 * The identity facts of one person of a repeated person group (SEP-293).
 *
 * `prefix` is the prefix of the group and the number of the person, for
 * example `stakeholder_2`. The ids of the group add one suffix to it, as
 * `personGroup` of `client-application-blocks.ts` names them.
 */
export function clientApplicationPersonIdentity(
    answers: ClientApplicationAnswers,
    prefix: string,
): ClientApplicationIdentity {
    const id = (suffix: string) => `${prefix}_${suffix}`;
    return identityFrom(answers, {
        title: id('title'),
        forenames: id('forenames'),
        surname: id('surname'),
        dateOfBirth: id('date_of_birth'),
        email: id('email_address'),
        phones: [
            id('mobile_number'),
            id('telephone_number'),
            id('home_number'),
            id('business_number'),
        ],
        houseNoName: id('current_address_house_no_name'),
        street: id('current_address_street'),
        townCity: id('current_address_town_city'),
        county: id('current_address_county'),
        postcode: id('current_address_postcode'),
        country: id('current_address_country'),
    });
}

/** The role of one person that the identity check covers (SEP-293). */
export type ClientApplicationPartyRole =
    | 'applicant'
    | 'stakeholder'
    | 'trust_party'
    | 'scheme_member';

/** One person that the identity check covers (SEP-293). */
export interface ClientApplicationParty {
    /** Stable inside one submission: `applicant`, `stakeholder_2`, and so on. */
    readonly partyKey: string;
    /** Each role of the person. One person can have two roles. */
    readonly roles: readonly ClientApplicationPartyRole[];
    /** The capacity that the person gave, for example "Director", or an empty string. */
    readonly capacity: string;
    readonly identity: ClientApplicationIdentity;
}

/** One person of a repeated person group, as the form of the group names it. */
export interface ClientApplicationPartySource {
    readonly partyKey: string;
    readonly role: ClientApplicationPartyRole;
    readonly capacity: string;
    readonly identity: ClientApplicationIdentity;
}

/** The name and the date of birth that tell two entries of one person apart. */
function personKey(identity: ClientApplicationIdentity): string {
    const name = identity.fullName.toLowerCase().replace(/\s+/g, ' ');
    return `${name}|${identity.dateOfBirth}`;
}

/**
 * Each person that the identity check covers, the applicant first (SEP-293).
 *
 * Two entries with the same full name and the same date of birth are one
 * person with two roles: a director who is also the nominated representative
 * is one person to check. A person with no name is not a party.
 */
export function clientApplicationParties(
    answers: ClientApplicationAnswers,
    others: readonly ClientApplicationPartySource[],
    applicantCapacityId?: string,
): readonly ClientApplicationParty[] {
    const applicant: ClientApplicationParty = {
        partyKey: 'applicant',
        roles: ['applicant'],
        capacity: applicantCapacityId === undefined ? '' : text(answers, applicantCapacityId),
        identity: clientApplicationIdentity(answers),
    };
    const parties: ClientApplicationParty[] = [applicant];
    const byPerson = new Map<string, number>([[personKey(applicant.identity), 0]]);
    for (const other of others) {
        if (other.identity.fullName === '') continue;
        const key = personKey(other.identity);
        const seen = byPerson.get(key);
        if (seen !== undefined) {
            const party = parties[seen]!;
            if (!party.roles.includes(other.role)) {
                parties[seen] = { ...party, roles: [...party.roles, other.role] };
            }
            continue;
        }
        byPerson.set(key, parties.length);
        parties.push({
            partyKey: other.partyKey,
            roles: [other.role],
            capacity: other.capacity,
            identity: other.identity,
        });
    }
    return parties;
}

/**
 * Whether the identity facts are enough to run a check and raise a case.
 *
 * The four facts are the surname, the forenames, the date of birth and the
 * email address. Each one is a REQUIRED question of the bank, so a complete
 * application always holds them. The guard exists because the submission must
 * refuse rather than create a nameless `Client`.
 */
export function clientApplicationIdentityIsUsable(identity: ClientApplicationIdentity): boolean {
    return (
        identity.surname.length > 0 &&
        identity.forenames.length > 0 &&
        identity.dateOfBirth.length > 0 &&
        identity.email.length > 0
    );
}
